// Custom hook for menu categorization state and handlers.
// Used by RestaurantCuisine (creation flow) and MenuRefreshPanel (refresh flow).
//
// API:
//   const categorization = useMenuCategorization(predictedOptions, onPredictedOptionsChange)
//
// - predictedOptions: the consumer owns this state
// - onPredictedOptionsChange(updater, metadata): callback to update predictedOptions.
//     updater: (prev) => next
//     metadata: { type, ...details } so the consumer can apply the same change to other state
//       (e.g. RestaurantCuisine syncing allMenuItems)

import { useState, useEffect } from "react";
import { CATEGORY_CONFIG, getSectionName, getSectionOrder } from "./menuCategorizationConstants";

const useMenuCategorization = (predictedOptions, onPredictedOptionsChange) => {
  const [menuItemCategories, setMenuItemCategories] = useState({});
  const [collapsedSections, setCollapsedSections] = useState({
    GUARANTEED_VEGAN: false,
    PROBABLY_VEGAN: false,
    MAYBE_VEGAN: false,
    PROBABLY_NOT_VEGAN: false,
  });
  const [editingSection, setEditingSection] = useState(null);
  const [editingSectionValue, setEditingSectionValue] = useState("");

  // Initialize menuItemCategories when predictedOptions change.
  // Only set defaults for NEW items — preserve existing user selections.
  useEffect(function initCategoriesWhenPredictedOptionsChange() {
    if (!predictedOptions) return;

    setMenuItemCategories((prev) => {
      const categories = { ...prev };

      // GUARANTEED_VEGAN items default to "vegan"
      (predictedOptions.GUARANTEED_VEGAN || []).forEach((item) => {
        if (!(item.option in categories)) {
          categories[item.option] = "vegan";
        }
      });

      // Other items default to "maybeVegan"
      [
        ...(predictedOptions.PROBABLY_VEGAN || []),
        ...(predictedOptions.MAYBE_VEGAN || []),
        ...(predictedOptions.PROBABLY_NOT_VEGAN || []),
      ].forEach((item) => {
        if (!(item.option in categories)) {
          categories[item.option] = "maybeVegan";
        }
      });

      return categories;
    });
  }, [predictedOptions]);

  const totalItems = predictedOptions
    ? (predictedOptions.GUARANTEED_VEGAN?.length || 0) +
      (predictedOptions.PROBABLY_VEGAN?.length || 0) +
      (predictedOptions.MAYBE_VEGAN?.length || 0) +
      (predictedOptions.PROBABLY_NOT_VEGAN?.length || 0)
    : 0;

  const veganCount = Object.values(menuItemCategories).filter(
    (cat) => cat === "vegan"
  ).length;

  const handleCategoryChange = (optionName, newCategory, originalCategory) => {
    setMenuItemCategories((prev) => {
      const updated = { ...prev, [optionName]: newCategory };

      // Auto-collapse section if all items are now categorized (not "maybeVegan")
      if (originalCategory && predictedOptions) {
        const sectionItems = predictedOptions[originalCategory] || [];
        const allCategorized = sectionItems.every((item) => {
          const currentCat =
            item.option === optionName
              ? newCategory
              : prev[item.option] || "maybeVegan";
          return currentCat !== "maybeVegan";
        });
        if (allCategorized) {
          setCollapsedSections((prevCollapsed) => ({
            ...prevCollapsed,
            [originalCategory]: true,
          }));
        }
      }

      return updated;
    });
  };

  const handleBulkCategoryChange = (items, category, originalCategory) => {
    const updates = {};
    items.forEach((item) => {
      updates[item.option] = category;
    });
    setMenuItemCategories((prev) => ({ ...prev, ...updates }));
    setCollapsedSections((prev) => ({ ...prev, [originalCategory]: true }));
  };

  const toggleSection = (sectionKey) => {
    setCollapsedSections((prev) => ({
      ...prev,
      [sectionKey]: !prev[sectionKey],
    }));
  };

  const getAvailableSections = () => {
    const sections = new Set();
    Object.values(predictedOptions || {}).forEach((items) => {
      if (!Array.isArray(items)) return;
      items.forEach((item) => {
        const sectionName = getSectionName(item.section);
        if (sectionName) sections.add(sectionName);
      });
    });
    return Array.from(sections).sort();
  };

  const handleSectionRename = (categoryKey, oldSectionName, newSectionName) => {
    if (
      !newSectionName ||
      newSectionName.trim() === "" ||
      oldSectionName === newSectionName
    ) {
      setEditingSection(null);
      setEditingSectionValue("");
      return;
    }

    onPredictedOptionsChange(
      (prev) => {
        const updated = { ...prev };
        Object.keys(updated).forEach((key) => {
          if (Array.isArray(updated[key])) {
            updated[key] = updated[key].map((item) => {
              const itemSectionName = getSectionName(item.section);
              if (itemSectionName === oldSectionName) {
                return {
                  ...item,
                  section:
                    typeof item.section === "object"
                      ? { ...item.section, name: newSectionName.trim() }
                      : newSectionName.trim(),
                };
              }
              return item;
            });
          }
        });
        return updated;
      },
      { type: "sectionRename", oldName: oldSectionName, newName: newSectionName.trim() }
    );

    setEditingSection(null);
    setEditingSectionValue("");
  };

  const handleItemSectionChange = (optionName, categoryKey, newSectionName) => {
    onPredictedOptionsChange(
      (prev) => {
        // Look up the target section's existing order (from any category)
        let targetOrder = null;
        if (newSectionName) {
          for (const key of Object.keys(prev)) {
            if (!Array.isArray(prev[key])) continue;
            for (const it of prev[key]) {
              if (getSectionName(it.section) === newSectionName) {
                targetOrder = getSectionOrder(it.section);
                if (targetOrder != null) break;
              }
            }
            if (targetOrder != null) break;
          }
        }

        const updated = { ...prev };
        if (updated[categoryKey]) {
          updated[categoryKey] = updated[categoryKey].map((item) => {
            if (item.option === optionName) {
              let newSection;
              if (!newSectionName) {
                newSection = null;
              } else if (typeof item.section === "object") {
                // Fresh object — don't carry old order; use target section's order if it exists
                newSection = targetOrder != null
                  ? { name: newSectionName, order: targetOrder }
                  : { name: newSectionName };
              } else {
                newSection = newSectionName;
              }
              return { ...item, section: newSection };
            }
            return item;
          });
        }
        return updated;
      },
      { type: "itemSectionChange", optionName, categoryKey, newSectionName }
    );
  };

  const handleOptionChange = (optionName, categoryKey, changes) => {
    onPredictedOptionsChange(
      (prev) => {
        const updated = { ...prev };
        if (updated[categoryKey]) {
          updated[categoryKey] = updated[categoryKey].map((item) => {
            if (item.option === optionName) {
              return { ...item, ...changes };
            }
            return item;
          });
        }
        return updated;
      },
      { type: "optionChange", optionName, categoryKey, changes }
    );

    // Update menuItemCategories key if option name changed
    if (changes.option && changes.option !== optionName) {
      setMenuItemCategories((prevCats) => {
        const newCats = { ...prevCats };
        if (newCats[optionName] !== undefined) {
          newCats[changes.option] = newCats[optionName];
          delete newCats[optionName];
        }
        return newCats;
      });
    }
  };

  // Build allMenuItems + veganOptions from predictedOptions + menuItemCategories
  const buildMenuResult = () => {
    const allMenuItems = [];
    CATEGORY_CONFIG.forEach(({ key }) => {
      (predictedOptions[key] || []).forEach((item) => {
        const menuItem = {
          option: item.option,
          description: item.description || "",
          section: item.section || null,
          category:
            menuItemCategories[item.option] ||
            (key === "GUARANTEED_VEGAN" ? "vegan" : "maybeVegan"),
          originalCategory: key,
          probability:
            item.probability || (key === "GUARANTEED_VEGAN" ? 1.0 : item.probability),
          source: item.source,
        };
        if (item.source_url) menuItem.source_url = item.source_url;
        allMenuItems.push(menuItem);
      });
    });

    const veganOptions = allMenuItems
      .filter((item) => item.category === "vegan")
      .map((item) => ({
        option: item.option,
        description: item.description,
        section: item.section,
      }));

    return { allMenuItems, veganOptions };
  };

  const handleDeleteItem = (optionName, categoryKey) => {
    onPredictedOptionsChange(
      (prev) => {
        const updated = { ...prev };
        if (updated[categoryKey]) {
          updated[categoryKey] = updated[categoryKey].filter(
            (item) => item.option !== optionName
          );
        }
        return updated;
      },
      { type: "deleteItem", optionName, categoryKey }
    );
    setMenuItemCategories((prev) => {
      const newCats = { ...prev };
      delete newCats[optionName];
      return newCats;
    });
  };

  const handleDeleteUrl = (url) => {
    // Collect option names to delete before mutating
    const toDelete = [];
    Object.values(predictedOptions || {}).forEach((items) => {
      if (!Array.isArray(items)) return;
      items.forEach((item) => {
        if (item.source_url === url) toDelete.push(item.option);
      });
    });

    onPredictedOptionsChange(
      (prev) => {
        const updated = { ...prev };
        Object.keys(updated).forEach((key) => {
          if (Array.isArray(updated[key])) {
            updated[key] = updated[key].filter((item) => item.source_url !== url);
          }
        });
        return updated;
      },
      { type: "deleteUrl", url }
    );

    setMenuItemCategories((prev) => {
      const newCats = { ...prev };
      toDelete.forEach((name) => delete newCats[name]);
      return newCats;
    });
  };

  const handleAddItem = () => {
    const newItem = { option: "", description: "", section: null };
    onPredictedOptionsChange(
      (prev) => ({
        ...prev,
        GUARANTEED_VEGAN: [...(prev.GUARANTEED_VEGAN || []), newItem],
      }),
      { type: "addItem" }
    );
    setMenuItemCategories((prev) => ({ ...prev, [""]: "vegan" }));
    setCollapsedSections((prev) => ({ ...prev, GUARANTEED_VEGAN: false }));
  };

  const resetCategories = () => {
    setMenuItemCategories({});
    setCollapsedSections({
      GUARANTEED_VEGAN: false,
      PROBABLY_VEGAN: false,
      MAYBE_VEGAN: false,
      PROBABLY_NOT_VEGAN: false,
    });
    setEditingSection(null);
    setEditingSectionValue("");
  };

  return {
    menuItemCategories,
    collapsedSections,
    editingSection,
    setEditingSection,
    editingSectionValue,
    setEditingSectionValue,
    totalItems,
    veganCount,
    handleCategoryChange,
    handleBulkCategoryChange,
    toggleSection,
    handleSectionRename,
    handleItemSectionChange,
    handleOptionChange,
    getAvailableSections,
    buildMenuResult,
    resetCategories,
    handleAddItem,
    handleDeleteItem,
    handleDeleteUrl,
  };
};

export default useMenuCategorization;
